import {
    TTrackPageEvent,
    TTrackCartEvent,
    TTrackAddToCartEvent,
    TTrackRemoveFromCartEvent,
    TTrackUser,
    TTrackSearchEvent,
    TTrackImpression,
    TTrackClick,
    TTrackCheckout,
    TTrackProductDetailFromRecommendation,
    TTrackTransaction,
    TCriteoCartItem,
} from '@com/core/common/types/tracking';

// import { TCart, TCartItem } from '@com/core/common/types/cart';
import { TCart, TCartItem } from '@com/core/common/types/cartDaily';

import { setCookie, getCookie } from '@com/core/common/utils/client';

import uuid from 'uuid/v4'; // DO NOT CHANGE. Check with @Nipun, @Gihan or Watchdog SVC for used version

import TagManager from 'react-gtm-module';
import { reportEvent } from './sentry';

import { MARKETPLACE_CONFIG } from './config';
import { APP } from '../baseConfig';
import { THit } from '../types/catalog';
import { TLocale } from '../types/locale';

export default class Analytics {
    static visitorId: string | null;

    static customerId: string | null;

    /**
     * Get Visitor ID, save in cookie
     * @function getVisitorId
     * @returns  {string}
     */
    static getVisitorId = (): string => {
        if (typeof window === 'undefined') {
            reportEvent('Analytics initialized on SSR', 'Analytics.getVisitorId', 'warning');
            return '';
        }

        let visitorId = '';
        try {
            visitorId = getCookie('visitor_id') || '';
        } catch (e) {
            return '';
        }
        if (visitorId !== '') {
            return visitorId;
        }
        visitorId = uuid();
        try {
            setCookie('visitor_id', visitorId);
            return visitorId;
        } catch (e) {
            reportEvent('Error while setting userId', 'Analytics.getVisitorId', 'error');
        }
        return visitorId;
    };

    /**
     * Initializes Analytics
     * @function initialize
     * @param {TLocale}
     * @returns {void}
     */
    public static initialize(locale: TLocale): void {
        if (typeof window === 'undefined') {
            return;
        }
        if (!this.visitorId) {
            this.visitorId = Analytics.getVisitorId();
            TagManager.initialize({
                gtmId: MARKETPLACE_CONFIG.gtm.gtmID,
                dataLayer: {
                    event: 'initUrl',
                    locale: locale.apiLocale,
                    vid: Analytics.visitorId,
                    visitorId: Analytics.visitorId,
                    initUrl: window.location.href,
                    googleAnalyticsId: MARKETPLACE_CONFIG.gtm.gaID,
                },
            });
        }
    }

    /**
     * Emits a trackPage event to GTM
     * @function trackPage
     * @param {TLocale}
     * @param {TTrackPageEvent | TTrackCartEvent} - Track page event details or Cart page event details
     * @returns {void}
     */
    public static trackPage = (locale: TLocale, params: TTrackPageEvent | TTrackCartEvent): void => {
        if (typeof window === 'undefined') {
            return;
        }

        try {
            if (!Analytics.visitorId) {
                Analytics.initialize(locale);
            }

            const updatedParams = Analytics.setStaticEventParams(locale, params);

            TagManager.dataLayer({
                dataLayer: updatedParams,
            });

            if (MARKETPLACE_CONFIG.enabledFeatures.etracking && MARKETPLACE_CONFIG.etracking.id) {
                const eTrackEvent = {
                    vid: Analytics.visitorId,
                    uid: Analytics.customerId,
                    ev: updatedParams.event,
                    em: updatedParams.eventAction,
                    lo: updatedParams.locale,
                    pf: updatedParams.platform,
                    pc: params.store || MARKETPLACE_CONFIG.etracking.id,
                };

                // Noon ETracking
                Analytics.etrack(eTrackEvent);
            }
        } catch (e) {
            reportEvent('Error during track page event', 'Analytics.trackPage', 'error', { error: e, params });
        }
    };

    /**
     * Emits a trackAddToCart event to GTM
     * @function trackAddToCart
     * @param {TLocale}
     * @param {TTrackAddToCartEvent} - Track add to cart event details
     * @returns {void}
     */
    public static trackAddToCart = (locale: TLocale, params: TTrackAddToCartEvent): void => {
        try {
            if (!Analytics.visitorId) {
                Analytics.initialize(locale);
            }

            TagManager.dataLayer({
                dataLayer: Analytics.setStaticEventParams(locale, params),
            });
        } catch (e) {
            reportEvent('Error during track add to cart event', 'Analytics.trackAddToCart', 'error', e);
        }
    };

    public static trackProductDetailFromRecommendation = (
        locale: TLocale,
        params: TTrackProductDetailFromRecommendation,
    ): void => {
        try {
            if (!Analytics.visitorId) {
                Analytics.initialize(locale);
            }

            TagManager.dataLayer({
                dataLayer: Analytics.setStaticEventParams(locale, params),
            });
        } catch (error) {
            reportEvent(
                'Error during track product visit from recommendations event',
                'Analytics.trackProductDetailFromRecommendation',
                'error',
                error,
            );
        }
    };

    /**
     * Emits a trackRemoveFromCart event to GTM
     * @function trackRemoveFromCart
     * @param {TLocale}
     * @param {TTrackRemoveFromCartEvent} - Track remove from cart event details
     * @returns {void}
     */
    public static trackRemoveFromCart = (locale: TLocale, params: TTrackRemoveFromCartEvent): void => {
        try {
            if (!Analytics.visitorId) {
                Analytics.initialize(locale);
            }
            TagManager.dataLayer({
                dataLayer: Analytics.setStaticEventParams(locale, params),
            });
        } catch (e) {
            reportEvent('Error during track remove from cart event', 'Analytics.trackRemoveFromCart', 'error', e);
        }
    };

    /**
     * Emits a trackUser event to GTM
     * @function trackUser
     * @param {TLocale}
     * @param {TTrackUser} - Track user event details
     * @returns {void}
     */
    public static trackUser = (locale: TLocale, params: TTrackUser): void => {
        if (typeof window === 'undefined') {
            return;
        }

        try {
            if (!Analytics.visitorId) {
                Analytics.initialize(locale);
            }
            if (
                params.event === 'user_sign_in' ||
                params.event === 'user_sign_up' ||
                params.event === 'user_logged_in'
            ) {
                Analytics.customerId = params.custId || null;
            } else {
                Analytics.customerId = null;
            }
            TagManager.dataLayer({
                dataLayer: Analytics.setStaticEventParams(locale, params),
            });
        } catch (e) {
            reportEvent('Error during track user event', 'Analytics.trackUser', 'error', e);
        }
    };

    /**
     * Emits a trackSearch event to GTM
     * @function trackSearch
     * @param {TLocale}
     * @param {TTrackSearchEvent} - Track search event details
     * @returns {void}
     */
    public static trackSearch = (locale: TLocale, params: TTrackSearchEvent): void => {
        try {
            if (!Analytics.visitorId) {
                Analytics.initialize(locale);
            }

            TagManager.dataLayer({
                dataLayer: {
                    event: 'search',
                    eventAction: params.eventAction,
                    eventLabel: params.eventLabel,
                    value: params.value,
                    locale: locale.apiLocale,
                    platform: params.platform,
                    currency: locale.currencyCodeEN,
                    custId: Analytics.customerId,
                    vid: Analytics.visitorId,
                },
            });
        } catch (e) {
            reportEvent('Error during track search event', 'Analytics.trackSearch', 'error', e);
        }
    };

    /**
     * Emits a trackPage event to GTM
     * @function trackImpression
     * @param {TLocale}
     * @param {TTrackImpression} - Track impression event details
     * @returns {void}
     */
    public static trackImpression = (locale: TLocale, params: TTrackImpression): void => {
        try {
            if (!Analytics.visitorId) {
                Analytics.initialize(locale);
            }

            TagManager.dataLayer({
                dataLayer: {
                    event: 'promotionView',
                    locale: locale.apiLocale,
                    platform: params.platform,
                    currency: locale.currencyCodeEN,
                    custId: Analytics.customerId,
                    vid: Analytics.visitorId,
                    ecommerce: params.ecommerce,
                },
            });
        } catch (e) {
            reportEvent('Error during track impression event', 'Analytics.trackImpression', 'error', e);
        }
    };

    /**
     * Emits a trackPage event to GTM
     * @function trackClick
     * @param {TLocale}
     * @param {TTrackClick} - Track click event details
     * @returns {void}
     */
    public static trackClick = (locale: TLocale, params: TTrackClick): void => {
        try {
            if (!Analytics.visitorId) {
                Analytics.initialize(locale);
            }

            TagManager.dataLayer({
                dataLayer: {
                    event: 'promotionClick',
                    locale: locale.apiLocale,
                    platform: params.platform,
                    currency: locale.currencyCodeEN,
                    custId: Analytics.customerId,
                    vid: Analytics.visitorId,
                    ecommerce: params.ecommerce,
                },
            });
        } catch (e) {
            reportEvent('Error during track click event', 'Analytics.trackClick', 'error', e);
        }
    };

    /**
     * Emits a trackPage event to GTM
     * @function trackCheckout
     * @param {TLocale}
     * @param {TTrackClick} - Track checkout event details
     * @returns {void}
     */
    public static trackCheckout = (locale: TLocale, params: TTrackCheckout): void => {
        try {
            if (!Analytics.visitorId) {
                Analytics.initialize(locale);
            }

            TagManager.dataLayer({
                dataLayer: Analytics.setStaticEventParams(locale, params),
            });
        } catch (e) {
            reportEvent('Error during track checkout event', 'Analytics.trackCheckout', 'error', e);
        }
    };

    /**
     * Emits a trackTransaction event to GTM
     * @function trackTransaction
     * @param {TLocale}
     * @param {TTrackTransaction} - Track add to cart event details
     * @returns {void}
     */
    public static trackTransaction = (locale: TLocale, params: TTrackTransaction): void => {
        try {
            if (!Analytics.visitorId) {
                Analytics.initialize(locale);
            }

            TagManager.dataLayer({
                dataLayer: {
                    googleAnalyticsId: MARKETPLACE_CONFIG.gtm.gaID,
                    ...Analytics.setStaticEventParams(locale, params),
                },
            });
        } catch (e) {
            reportEvent('Error during track transaction event', 'Analytics.trackTransaction', 'error', e);
        }
    };

    /**
     * Formats TCart object items into the appropriate format to be sent to GTM
     * @function formatCartItems
     * @param {TCart} - Cart object
     * @returns {any} - Array of cart items
     */
    public static formatCartItems = (data: TCart): { skuConfig: string; price: number; quantity: number }[] | null => {
        if (!data || !data.items) {
            return [];
        }

        const cartItems = [] as TCartItem[];
        data.items.forEach((item: TCartItem) => {
            if (item.kind === 'bundle') {
                cartItems.push(...item.items);
            } else {
                cartItems.push(item);
            }
        });

        try {
            return cartItems.map((item: TCartItem) => ({
                skuConfig: item.skuConfig,
                price: item.unitPrice,
                quantity: item.quantity,
            }));
        } catch (e) {
            reportEvent('Error getting cart items', 'Analytics.formatCartItems', 'error', e);
        }
        return null;
    };

    /**
     * Formats TCart object items into the appropriate format to be sent to Criteo via GTM
     * @function formatCriteoCartItems
     * @param {TCart} - Cart object
     * @returns {any} - Array of cart items
     */
    public static formatCriteoCartItems = (data: TCart): TCriteoCartItem[] | undefined => {
        try {
            return data.items.map((item: TCartItem) => ({
                id: item.skuConfig,
                price: item.unitPrice,
                quantity: item.quantity,
            }));
        } catch (e) {
            reportEvent('Error getting cart items', 'Analytics.formatCriteoCartItems', 'error', e);
        }
        return undefined;
    };

    /**
     * Formats TCart object items into the appropriate format to be sent to Criteo via GTM
     * @function formatFacebookItems
     * @param {TCart} - Cart object
     * @returns {any} - Array of cart items
     */
    public static formatFacebookItems = (
        data: TCart,
    ): Array<{ id: string; item_price: TCartItem['unitPrice']; quantity: TCartItem['quantity'] }> | undefined => {
        try {
            return data.items.map((item: TCartItem) => ({
                id: item.skuConfig,
                item_price: item.unitPrice,
                quantity: item.quantity,
            }));
        } catch (e) {
            reportEvent('Error getting cart items', 'Analytics.formatFacebookItems', 'error', e);
        }
        return undefined;
    };

    static setStaticEventParams = (locale: TLocale, event: Record<string, unknown>): any => {
        const updatedEvent = {
            ...event,
            platform: 'web',
            locale: locale.apiLocale,
            timestamp: Date.now().toString(),
            currency: locale.currencyCodeEN,
            mpCode: 'daily',
            vid: Analytics.visitorId,
            visitor_id: Analytics.visitorId,
            visitorId: Analytics.visitorId,
        };
        return updatedEvent;
    };

    static serialize = (obj: Record<string, unknown>, prefix?: string): string => {
        const str = [];
        let p;

        // eslint-disable-next-line no-restricted-syntax
        for (p in obj) {
            // eslint-disable-next-line no-prototype-builtins
            if (obj.hasOwnProperty(p)) {
                const k = prefix ? `${prefix}[${p}]` : p;
                const v = obj[p];

                if (typeof v === 'object' && v !== null && Object.keys(v).length > 0) {
                    str.push(Analytics.serialize(v as Record<string, unknown>, k));
                } else {
                    str.push(`${encodeURIComponent(k)}=${encodeURIComponent(v as string)}`);
                }
            }
        }

        return str.join('&');
    };

    static etrack(event: Record<string, string | null>): void {
        const pixel = new Image();
        pixel.src = `${MARKETPLACE_CONFIG.etracking.url}?${Analytics.serialize(event)}`;
    }

    static formatFirstThreeSkuConfigs = (
        items: THit[],
    ): Record<'skuConfig' | 'skuConfig2' | 'skuConfig3', string | null> | [] => {
        if (items && items.length > 0) {
            return {
                skuConfig: items[0] && items[0].sku_config ? items[0].sku_config : null,
                skuConfig2: items[1] && items[1].sku_config ? items[1].sku_config : null,
                skuConfig3: items[2] && items[2].sku_config ? items[2].sku_config : null,
            };
        }
        return [];
    };

    public static trackWebVitals = (id: string, name: string, label: string, value: number): void => {
        TagManager.dataLayer({
            dataLayer: {
                event: `web-vitals`,
                event_category: 'Web Vitals',
                value: Math.round(name === 'CLS' ? value * 1000 : value),
                event_value: Math.round(name === 'CLS' ? value * 1000 : value),
                event_action: name,
                event_label: id,
                non_interaction: true,
                mpCode: 'daily',
                app: APP,
            },
        });
    };
}

// #TODO: Keeping for reference, replaced by util. Delete later
// /**
//  * Initializes Google Tag Manager
//  * @function initializeGTM
//  * @returns {string} - html string with GTM initialization logic
//  */
// public static initializeGTM = (marketplaceConfig: TMarketplaceConfig = MARKETPLACE_CONFIG): { __html: string } => {
//     const { visitorId } = Analytics.analytics;
//     return {
//         __html: `
//         (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
//         new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
//         j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
//         'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
//         })(window,document,'script','dataLayer','${marketplaceConfig.gtm.gtmID}');
//         dataLayer.push({
//             event: "initUrl",
//             locale: '${Locale.getISOLocale()}',
//             visitorId: '${visitorId}',
//             initUrl: window.location.href,
//             googleAnalyticsId: '${marketplaceConfig.gtm.gaID}',
//         });`,
//     };
// };

// /**
//  * Initializes Google Tag Manager without script
//  * @function initializeGTMNoScript
//  * @returns {string} - html string with GTM initialization logic
//  */
// public static initializeGTMNoScript = (
//     marketplaceConfig: TMarketplaceConfig = MARKETPLACE_CONFIG,
// ): { __html: string } => {
//     return {
//         __html: `<iframe src="https://www.googletagmanager.com/ns.html?id=${marketplaceConfig.gtm.gtmID}"
//         height="0" width="0" style="display:none;visibility:hidden"></iframe>`,
//     };
// };
